import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import IconButton from '@mui/material/IconButton';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { IDataTableColumn } from 'react-data-table-component';
import { SalesEstimateDocument } from '../model';
import axios from '../../../sharedModules/utils/axios.utils';
import DataTable from '../../../sharedModules/dataTable';

interface JobFolderProps {
  documents: SalesEstimateDocument[];
}

const JobFolder: React.FC<JobFolderProps> = (props) => {
  let { documents } = props;

  let fileLabel = 'files';
  if (documents?.length === 1) {
    fileLabel = 'file';
  }

  const downloadSingleFile = async (row) => {
    try {
      let response: any = await axios.get(`/sales_estimates/sales_estimate_documents/${row.id}/download`, {
        responseType: 'blob',
      });

      if (response.status === 200) {
        let blob = new Blob([response.data], { type: response.data.type });
        let link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = row?.description || 'download.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error(`Failed to fetch ${row?.description}`);
      }
    } catch (error) {
      console.error(`Failed to fetch ${row?.description}`, error);
    }
  };

  const downloadZipFolder = async () => {
    const zipFolder = new JSZip();

    try {
      for (let document of documents) {
        const response = await axios.get(`/sales_estimates/sales_estimate_documents/${document.id}/download`, {
          params: { url: document.url },
          responseType: 'blob',
        });

        if (response.status !== 200) {
          throw new Error(`Failed to fetch ${document.description}`);
        }

        // Add the blob to the zip folder
        const blob = response.data;
        zipFolder.file(`${document.description}.pdf`, blob);
      }

      // Generate the zip file after all files have been added
      const zipBlob = await zipFolder.generateAsync({ type: 'blob' });

      saveAs(zipBlob, 'job_folder.zip');
    } catch (error) {
      console.error('Error downloading zip folder:', error);
    }
  };

  let jobFolderColumns: IDataTableColumn<any>[] = [
    {
      name: 'Description',
      selector: 'description',
      grow: 2,
    },
    {
      name: 'Last Updated',
      selector: 'updated_at',
      grow: 2,
    },
    {
      name: 'Download',
      selector: 'url',
      cell: (row) => (
        <IconButton onClick={() => downloadSingleFile(row)} aria-label="download">
          <DownloadIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Accordion>
      <AccordionSummary>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography>
              Job Folder — {documents?.length} {fileLabel}
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={downloadZipFolder}>
              Download Zip Folder
            </Button>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails style={{ marginTop: -30 }}>
        <Grid container direction="column" spacing={2}>
          <Grid item xs={12}>
            <DataTable
              noheader
              columns={jobFolderColumns}
              data={documents}
              striped
              dense
              highlightOnHover
              pointerOnHover
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default JobFolder;
